import { AxiosResponse } from "axios";
import laravelApi from "./api";
import { CreateNetSuiteQuotePayload } from "@/models/netsuite/netsuite";
import {
  DriftReport,
  QuoteDraft,
  QuoteDraftPayload,
  SalesOrderDuplicatePreview,
  SalesOrderListResult,
  SalesOrderSummary,
  SubmitQuoteDraftResponse
} from "@/models/quote_draft/quote_draft";

/**
 * FUPOF-191 — Quote drafts persisted in the Laravel API's PostgreSQL DB.
 * All endpoints live under /api/quotes (auth:sanctum). Drafts are shared across
 * a sales team: the API returns the authenticated user's quotes plus those of
 * every teammate that shares their team leader. Only a draft's creator (or an
 * admin) may delete it; any teammate may view, edit, and submit.
 */
export default class QuoteDraftService {
  /** List the caller's quotes and their whole sales team's (drafts and submitted). */
  public async listQuotes(): Promise<QuoteDraft[]> {
    try {
      const res: AxiosResponse = await laravelApi.get("/api/quotes");
      return res.data.quotes ?? res.data.data ?? res.data ?? [];
    } catch (err) {
      throw this.wrap(err, "Unknown error listing quote drafts.");
    }
  }

  public async createQuote(payload: QuoteDraftPayload): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.post("/api/quotes", payload);
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error saving quote draft.");
    }
  }

  public async getQuote(id: number): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.get(`/api/quotes/${id}`);
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error loading quote draft.");
    }
  }

  /** Replace-all update of header + lines. */
  public async updateQuote(
    id: number,
    payload: QuoteDraftPayload
  ): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.put(
        `/api/quotes/${id}`,
        payload
      );
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error saving quote draft.");
    }
  }

  public async deleteQuote(id: number): Promise<void> {
    try {
      await laravelApi.delete(`/api/quotes/${id}`);
    } catch (err) {
      throw this.wrap(err, "Unknown error deleting quote draft.");
    }
  }

  /**
   * Submit the draft to NetSuite. The client builds the same
   * CreateNetSuiteQuotePayload shape used by the direct create_quote path, but
   * sends no custcol_* fields: the API owns line identity and injects the
   * NetSuite custom columns from PSQL itself. It forwards the payload to NS,
   * stores netsuite_quote_id / ns_line_ids, flips status to submitted, and
   * returns the persisted draft with those ids populated. When the draft
   * already has a netsuite_quote_id, the API updates the existing NS quote
   * instead of creating a new one.
   */
  public async submitQuote(
    id: number,
    payload: CreateNetSuiteQuotePayload
  ): Promise<SubmitQuoteDraftResponse> {
    try {
      const res: AxiosResponse = await laravelApi.post(
        `/api/quotes/${id}/submit`,
        payload
      );
      return res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error submitting quote to NetSuite.");
    }
  }

  /**
   * Request that this quote be processed into a NetSuite Sales Order. Same
   * payload shape as {@link submitQuote}: the API create/updates the estimate
   * (auto-submit) AND flags the RESTlet to email the order desk, then flips the
   * draft to `pending_sales_order` (which locks it from further edits).
   */
  public async requestSalesOrder(
    id: number,
    payload: CreateNetSuiteQuotePayload,
    impersonatorName: string | null = null
  ): Promise<SubmitQuoteDraftResponse> {
    try {
      const body = impersonatorName
        ? { ...payload, impersonator_name: impersonatorName }
        : payload;
      const res: AxiosResponse = await laravelApi.post(
        `/api/quotes/${id}/request-sales-order`,
        body
      );
      return res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error requesting a sales order.");
    }
  }

  /** Re-fetch NS state for a submitted quote and return the drift report. */
  public async reloadQuote(id: number): Promise<DriftReport> {
    try {
      const res: AxiosResponse = await laravelApi.get(
        `/api/quotes/${id}/reload`
      );
      return res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error reloading quote from NetSuite.");
    }
  }

  /**
   * Adopt a pre-existing NS quote (no PSQL record) into PSQL for editing.
   * The API creates the quotes/quote_lines rows from NS state, with every
   * line best-effort SKU-parsed and flagged parsed: true.
   */
  public async adoptQuote(netsuiteQuoteId: number): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.post("/api/quotes/adopt", {
        netsuite_quote_id: netsuiteQuoteId
      });
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error adopting NetSuite quote.");
    }
  }

  /**
   * Read-only sibling of {@link adoptQuote}: hydrates the same shape from a
   * pre-existing NS quote but persists nothing. The returned quote (and each of
   * its lines) comes back with id/created_at null by design — it's a transient
   * copy for the duplicate flow to seed the cart from, so no PSQL row is written
   * until the user saves the duplicate as its own draft.
   */
  public async previewAdoptQuote(
    netsuiteQuoteId: number
  ): Promise<QuoteDraft> {
    try {
      const res: AxiosResponse = await laravelApi.post(
        "/api/quotes/adopt/preview",
        { netsuite_quote_id: netsuiteQuoteId }
      );
      return res.data.quote ?? res.data.data ?? res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error previewing NetSuite quote.");
    }
  }

  /**
   * FUPOF-200 — read-only sibling of {@link previewAdoptQuote}, sourced from a
   * Sales Order instead of an Estimate. Builds an unsaved draft repriced to
   * today's catalog and the customer's current price level; nothing is
   * persisted until the rep's first Save Draft, so no phantom row is left
   * behind if they abandon the reorder.
   *
   * Unlike every other method in this file this returns the WHOLE envelope
   * rather than `res.data.quote` — the caller needs `stale_lines` to warn the
   * rep how many lines want a look before the quote goes out.
   */
  public async previewDuplicateSalesOrder(
    salesOrderId: number
  ): Promise<SalesOrderDuplicatePreview> {
    try {
      const res: AxiosResponse = await laravelApi.post(
        "/api/quotes/sales-orders/duplicate/preview",
        { netsuite_sales_order_id: salesOrderId }
      );
      return res.data;
    } catch (err) {
      throw this.wrap(err, "Unknown error building a quote from that sales order.");
    }
  }

  /**
   * FUPOF-200 — duplicatable Sales Orders. The API applies a DIFFERENT gate
   * depending on which question is being asked, which is why one method serves
   * two quite different lists:
   *
   * - with `customer_id`: scoped by the CUSTOMER's current rep, so a customer
   *   reassigned between sales reps still returns its full order history under
   *   whoever owns it now. This is the reorder case the feature exists for.
   * - without: scoped by the SALES ORDER's own rep, so "My Sales Orders" stays
   *   strictly the caller's own book.
   *
   * Quote admins bypass both. The API sorts newest-first and returns one page
   * (`page`, `per_page` default 50) with the `total` matching, so the views page
   * through the API rather than holding the whole list.
   *
   * `search` matches a customer name, Sales Order # or PO # across the caller's
   * whole scope, under the same rep gate, and is paged the same way.
   */
  public async listSalesOrders(
    filters: {
      customer_id?: number;
      since?: string;
      page?: number;
      per_page?: number;
      search?: string;
    } = {}
  ): Promise<SalesOrderListResult> {
    try {
      const res: AxiosResponse = await laravelApi.get(
        "/api/quotes/sales-orders",
        { params: filters }
      );
      const data = res.data ?? {};
      const salesOrders: SalesOrderSummary[] =
        data.sales_orders ?? data.data ?? (Array.isArray(data) ? data : []);
      return {
        sales_orders: salesOrders,
        // An API without paging returns neither; its rows are then all there is.
        total: typeof data.total === "number" ? data.total : salesOrders.length,
        page: typeof data.page === "number" ? data.page : filters.page ?? 1,
        per_page:
          typeof data.per_page === "number"
            ? data.per_page
            : filters.per_page ?? salesOrders.length
      };
    } catch (err) {
      throw this.wrap(err, "Unknown error listing sales orders.");
    }
  }

  private wrap(err: unknown, fallback: string): Error {
    const e = err as any;
    console.error("[QuoteDraftService]", e?.response?.data ?? e);
    return new Error(e?.response?.data?.message ?? e?.message ?? fallback);
  }
}
